import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { MIN_AGENT_REPLACEMENT_COST } from '@ourbranch/lookups';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Divider from 'core/components/divider';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { Radio } from 'core/components/radio';
import { useToast } from 'core/components/toast';
import { currencyFormatter } from 'core/helpers/formatters';

import useStyles from './rce-modal.styles';

const REPLACEMENT_COST_SOURCE = {
  CALCULATED: 'CALCULATED',
  AGENT: 'AGENT'
};

const RCEModal = ({ onClose, onSubmit, calculatedReplacementCost, agentReplacementCost, useCalculated }) => {
  const classes = useStyles();
  const toast = useToast();

  const validate = (values) => {
    if (values.source !== REPLACEMENT_COST_SOURCE.AGENT) {
      return {};
    }
    if (!values.agentReplacementCost) {
      return { agentReplacementCost: 'Enter a replacement cost estimate' };
    }
    if (values.agentReplacementCost < MIN_AGENT_REPLACEMENT_COST) {
      return {
        agentReplacementCost: `RCE must be at least ${currencyFormatter(MIN_AGENT_REPLACEMENT_COST, { removeDecimal: true })}`
      };
    }
    return {};
  };

  const handleSubmit = (values) => {
    onSubmit({
      useCalculated: values.source === REPLACEMENT_COST_SOURCE.CALCULATED,
      agentReplacementCost:
        values.source === REPLACEMENT_COST_SOURCE.AGENT ? values.agentReplacementCost : agentReplacementCost
    });
    onClose();
  };

  return (
    <BaseDialog open onClose={onClose} size="md" className={classes.modal}>
      <Formik
        initialValues={{
          source: useCalculated ? REPLACEMENT_COST_SOURCE.CALCULATED : REPLACEMENT_COST_SOURCE.AGENT,
          agentReplacementCost: agentReplacementCost ?? undefined
        }}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, submitForm, validateForm }) => {
          const usingAgentValue = values.source === REPLACEMENT_COST_SOURCE.AGENT;
          const handleSourceChange = (event) => setFieldValue('source', event.target.value);

          const handleDone = async () => {
            const validationErrors = await validateForm();
            if (Object.keys(validationErrors).length > 0) {
              toast.notify({ type: 'error', message: 'Fix the highlighted fields to continue.' });
            }
            submitForm();
          };

          return (
            <Form className={classes.content}>
              <Label type="subtitle" className={classes.title}>
                Replacement cost estimate (RCE)
              </Label>
              <Label type="body1">
                Our trusted third-party service calculates RCE from this quote&apos;s home characteristics and local
                market factors. You can use your own value until you run reports &mdash; update the home characteristics
                anytime and we&apos;ll recalculate our estimate.
              </Label>
              <Label type="body1Bold" className={classes.question}>
                Which value should we use until reports are run?
              </Label>
              <div>
                <Divider className={classes.divider} />
                <div className={classes.option}>
                  <Radio
                    id="source-calculated"
                    name="source"
                    value={values.source}
                    onChange={handleSourceChange}
                    options={[
                      {
                        value: REPLACEMENT_COST_SOURCE.CALCULATED,
                        label: <Label type="modalBody">Our calculated RCE value</Label>
                      }
                    ]}
                    radioClassName={classes.radio}
                    optionsSideBySide={false}
                  />
                  <Label type="infoSubtitleSmallLight" className={classes.calculatedValue}>
                    {calculatedReplacementCost
                      ? currencyFormatter(calculatedReplacementCost, { removeDecimal: true })
                      : 'N/A'}
                  </Label>
                </div>

                {!usingAgentValue && (
                  <NotificationCard className={classes.note} iconSize="icon16" type="infoBeige" noShadow>
                    <Label type="body3">
                      We&apos;ll automatically update this each time you edit the home&apos;s characteristics and update
                      the offer.
                    </Label>
                  </NotificationCard>
                )}

                <Divider className={classes.divider} />
                <div className={classes.option}>
                  <Radio
                    id="source-agent"
                    name="source"
                    value={values.source}
                    onChange={handleSourceChange}
                    options={[
                      { value: REPLACEMENT_COST_SOURCE.AGENT, label: <Label type="modalBody">Your RCE value</Label> }
                    ]}
                    radioClassName={classes.radio}
                    optionsSideBySide={false}
                  />
                  <div className={classes.agentField}>
                    <Field
                      type="numeric"
                      id="agentReplacementCost"
                      name="agentReplacementCost"
                      mode="light"
                      format={{ thousandSeparator: true, prefix: '$', decimalScale: 0 }}
                      disabled={!usingAgentValue}
                      className="no-min-height"
                      noMarginRight
                    />
                  </div>
                </div>

                {usingAgentValue && (
                  <NotificationCard className={classes.note} iconSize="icon16" type="infoBeige" noShadow>
                    <Label type="body3">
                      Once reports are run, we&apos;ll lock the RCE to our value. Please make sure the home&apos;s
                      characteristics are as accurate as possible.
                    </Label>
                  </NotificationCard>
                )}
                <Divider className={classes.divider} />
              </div>
              <div className={classes.actions}>
                <Button variant="contained" color="secondary" onClick={onClose} className={classes.action}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" className={classes.action} onClick={handleDone}>
                  Done
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </BaseDialog>
  );
};

RCEModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  calculatedReplacementCost: PropTypes.number,
  agentReplacementCost: PropTypes.number,
  useCalculated: PropTypes.bool
};

RCEModal.defaultProps = {
  calculatedReplacementCost: undefined,
  agentReplacementCost: undefined,
  useCalculated: false
};

export default RCEModal;
